import { useQuery } from "@tanstack/react-query";
import {
  TutorialJourneyStatus,
  TutorialJourneyType,
} from "@growth-machine-llc/stridist-api-client";
import React, { useMemo } from "react";
import NoteEditor from "../../new-editor/NoteEditor";
import { TUTORIAL_JOURNEY_SINGLE_KEY } from "../../../routes/admin/onboardingTemplate/JourneyTemplate";
import TutorialJourneysService from "../../../services/TutorialJourneysService";
import { fixLegacyContent } from "../../editor/utils/common";

const safeParse = (json?: string): unknown => {
  if (!json) return undefined;
  try {
    return JSON.parse(json);
  } catch {
    return undefined;
  }
};

const toEditorValue = (raw: unknown) => {
  if (Array.isArray(raw)) return fixLegacyContent(raw as any[]);
  return [];
};

export const useJoyrideSteps = (journeyType: TutorialJourneyType) => {
  const { data: tutorialJourney, isLoading: tutorialJourneyLoading } = useQuery(
    {
      queryKey: [TUTORIAL_JOURNEY_SINGLE_KEY, { type: journeyType }],
      queryFn: () =>
        TutorialJourneysService.getTutorialJourneyByType(journeyType),
      retry: false,
    },
  );

  const activatorContent = useMemo<React.ReactNode | null>(() => {
    const raw = safeParse(tutorialJourney?.journeyActivator?.content);
    if (!raw) return null;
    const parsed = toEditorValue(raw);
    return (
      <NoteEditor
        value={parsed}
        readOnly
        onChange={() => {}}
        style={{
          border: "none",
          background: "transparent",
          minHeight: 60,
          minWidth: 200,
        }}
      />
    );
  }, [tutorialJourney?.journeyActivator?.content]);

  const journeyActivatorProfile =
    tutorialJourney?.journeyActivator?.profile || null;
  const journeyActivatorId = tutorialJourney?.journeyActivator?.id || null;
  const journeyActivatorInteractions =
    tutorialJourney?.journeyActivator?.interactions;

  const steps = useMemo(() => {
    if (!tutorialJourney?.steps) return [];
    return tutorialJourney.steps.map((step, index) => {
      const raw = safeParse(step.content);
      const parsedContent = toEditorValue(raw);
      return {
        content: (
          <NoteEditor
            value={parsedContent}
            readOnly
            onChange={() => {}}
            style={{
              border: "none",
              background: "transparent",
              minHeight: 60,
              minWidth: 200,
            }}
          />
        ),
        disableBeacon: true,
        key: `step-${index}`,
        meta: { profile: step?.profile, anchor: step?.anchor },
      };
    });
  }, [tutorialJourney?.steps]);

  return {
    steps,
    activatorContent,
    tutorialJourneyLoading,
    journeyActivatorProfile,
    journeyActivatorId,
    journeyActivatorInteractions,
    journeyStatus: tutorialJourney?.status as TutorialJourneyStatus | undefined,
  };
};
